"use client";

import { getImageUrl, uploadFormFile } from "@/lib/database/client/database";
import { useEnterprise } from "@/lib/enterprise/EnterpriseProvider";
import { DEFAULT_FORM_LOGO, isSvgLogoUrl } from "@/lib/forms/logo-utils";
import { SupabaseClient } from "@supabase/supabase-js";
import { FormField } from "@/lib/database/forms";
import { useCallback, useEffect, useState } from "react";

export interface FormLogo {
  /** Resolved logo URL: custom logo, else enterprise brand, else the Nowadays default. */
  logoUrl: string;
  /** Whether the logo must render unoptimized (SVGs — next/image rejects them). */
  unoptimized: boolean;
  /** `onError` handler that degrades a broken/expired logo URL to the brand default. */
  onError: () => void;
}

/**
 * Custom hook for fetching and managing the form logo.
 * Priority: 1) Custom logo (per-form custom_logo_path), 2) Enterprise logo (the default
 * on enterprise subdomains), 3) Default Nowadays logo.
 *
 * Returns the resolved URL alongside the `unoptimized` flag and an `onError` handler, so
 * every consumer renders the logo identically without re-deriving them.
 */
export function useFormLogo(customLogoPath: string | null, supabase: SupabaseClient): FormLogo {
  const { logo: enterpriseLogo } = useEnterprise();
  const enterpriseFallback = enterpriseLogo?.src ?? DEFAULT_FORM_LOGO;
  const [logoUrl, setLogoUrl] = useState<string>(enterpriseFallback);

  useEffect(() => {
    let cancelled = false;

    async function resolveLogo() {
      // A per-form custom logo always wins when one is set...
      if (customLogoPath && customLogoPath.trim() !== "") {
        const url = await getImageUrl(supabase, customLogoPath);
        if (!cancelled) setLogoUrl(url || enterpriseFallback);
        return;
      }
      // ...otherwise default to the enterprise logo (enterprise subdomains), else the
      // default Nowadays logo.
      if (!cancelled) setLogoUrl(enterpriseFallback);
    }

    resolveLogo();
    return () => {
      cancelled = true;
    };
  }, [customLogoPath, supabase, enterpriseFallback]);

  // A resolved custom-logo URL that 404s or expires at load time degrades to the brand
  // default instead of leaving a broken-image glyph on the page.
  const onError = useCallback(() => {
    setLogoUrl((current) => (current === enterpriseFallback ? current : enterpriseFallback));
  }, [enterpriseFallback]);

  return { logoUrl, unoptimized: isSvgLogoUrl(logoUrl), onError };
}

/**
 * Custom hook for handling form file uploads
 * Manages uploading state and handles old file deletion
 */
export function useFormFileUpload(supabase: SupabaseClient, formId: number) {
  const [uploadingFields, setUploadingFields] = useState<{ [key: string]: boolean }>({});

  const uploadFile = async (
    field: FormField,
    file: File,
    oldFilePath?: string,
    userId?: number | null
  ): Promise<string | null> => {
    setUploadingFields((prev) => ({ ...prev, [field.label]: true }));

    try {
      // Delete old file if it exists
      if (oldFilePath && typeof oldFilePath === "string") {
        const { error: deleteError } = await supabase.storage.from("forms").remove([oldFilePath]);
        if (deleteError) {
          console.warn("Failed to delete old file:", deleteError);
          // Continue with upload even if deletion fails
        }
      }

      // Upload new file
      const filePath = await uploadFormFile(supabase, formId, userId || null, field.id, file);

      // Only mark as not uploading on success
      setUploadingFields((prev) => ({ ...prev, [field.label]: false }));

      return filePath;
    } catch (error) {
      console.error("Error uploading file:", error);
      // Reset uploading state on error
      setUploadingFields((prev) => ({ ...prev, [field.label]: false }));
      throw error;
    }
  };

  return { uploadFile, uploadingFields };
}
